import { motion, useReducedMotion } from "framer-motion";

/**
 * FCIO — "The Bridge"
 * Three lanes (Board / CIO / Delivery). The CIO row is the bridge:
 * decisions flow down, delivery signal flows up, both meet at the center.
 * Reflects the Fractional CIO promise: senior leadership between the board and the work.
 */
export default function FractionalCIONetwork({ className = "" }) {
  const reduce = useReducedMotion();
  const laneY = { board: 60, cio: 160, delivery: 260 };
  const width = 400;
  const lanes = [
    { y: laneY.board,    label: "BOARD",    accent: "#7DB4FF" },
    { y: laneY.cio,      label: "CIO",      accent: "#93C5FD", big: true },
    { y: laneY.delivery, label: "DELIVERY", accent: "#7DB4FF" },
  ];

  return (
    <div className={`pointer-events-none select-none ${className}`} aria-hidden="true">
      <svg viewBox="0 0 400 320" className="h-full w-full" role="presentation">
        <defs>
          <linearGradient id="fcio-lane" x1="0" x2="1">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0" />
            <stop offset="30%" stopColor="#3B82F6" stopOpacity="0.6" />
            <stop offset="70%" stopColor="#3B82F6" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
          </linearGradient>
          <radialGradient id="fcio-hub" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Lanes */}
        {lanes.map((l) => (
          <g key={l.label}>
            <line x1="40" y1={l.y} x2={width - 40} y2={l.y} stroke="url(#fcio-lane)" strokeWidth="1" />
            <text x="40" y={l.y - 12} fill="#94A3B8" fontSize="10" letterSpacing="2" fontFamily="ui-sans-serif, system-ui" style={{ textTransform: "uppercase" }}>
              {l.label}
            </text>
          </g>
        ))}

        {/* Central hub glow on the CIO lane */}
        <circle cx={width / 2} cy={laneY.cio} r="60" fill="url(#fcio-hub)" />

        {/* Vertical connectors (board <-> cio <-> delivery) */}
        {[width * 0.32, width * 0.5, width * 0.68].map((x, i) => (
          <line key={i} x1={x} y1={laneY.board} x2={x} y2={laneY.delivery} stroke="#7DB4FF" strokeOpacity="0.18" strokeDasharray="2 4" />
        ))}

        {/* Down-flowing dot: Board -> CIO -> Delivery */}
        {!reduce && (
          <motion.circle
            r="3" fill="#93C5FD"
            initial={{ cx: width * 0.32, cy: laneY.board, opacity: 0 }}
            animate={{
              cx: [width * 0.32, width * 0.32, width * 0.32],
              cy: [laneY.board, laneY.cio, laneY.delivery],
              opacity: [0, 1, 0],
            }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
          />
        )}

        {/* Up-flowing dot: Delivery -> CIO -> Board */}
        {!reduce && (
          <motion.circle
            r="3" fill="#7DB4FF"
            initial={{ cx: width * 0.68, cy: laneY.delivery, opacity: 0 }}
            animate={{
              cx: [width * 0.68, width * 0.68, width * 0.68],
              cy: [laneY.delivery, laneY.cio, laneY.board],
              opacity: [0, 1, 0],
            }}
            transition={{ duration: 4.4, repeat: Infinity, ease: "easeInOut", delay: 1.8 }}
          />
        )}

        {/* CIO node — the bridge — pulses gently */}
        <motion.circle
          cx={width / 2} cy={laneY.cio}
          fill="#3B82F6"
          initial={{ r: 10, opacity: 0.85 }}
          animate={reduce ? { r: 10 } : { r: [10, 12, 10] }}
          transition={reduce ? { duration: 0 } : { duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
        />
        <circle cx={width / 2} cy={laneY.cio} r="4" fill="#EFF6FF" />

        {/* Board + Delivery anchor nodes */}
        <circle cx={width * 0.32} cy={laneY.board} r="4.5" fill="#7DB4FF" />
        <circle cx={width * 0.68} cy={laneY.delivery} r="4.5" fill="#7DB4FF" />

        {/* Static side labels */}
        <text x={width / 2} y={laneY.cio + 30} textAnchor="middle" fill="#CBD5E1" fontSize="10" letterSpacing="2" fontFamily="ui-sans-serif, system-ui" style={{ textTransform: "uppercase" }}>
          THE BRIDGE
        </text>
      </svg>
    </div>
  );
}
